import { useCallback, useContext, useEffect, useRef } from "react";
import Hls from "hls.js";
import { PlayerContext } from "../../../../App";
import { artworkSize, spaceId } from "../../../basicInfo";
import { syncSpaceToProgramDateTime } from "../utils/trackSync";

const streamUrl = `https://listen.atomic.radio/${spaceId}.m3u8`;

const getAudioElement = (audioController, volume) => {
    if (!audioController.current) {
        audioController.current = new Audio();
    }

    audioController.current.volume = volume;
    return audioController.current;
};

const destroyHls = (audioController) => {
    if (!audioController?.hls) return;

    try {
        audioController.hls.stopLoad();
        audioController.hls.detachMedia();
        audioController.hls.destroy();
    } catch (error) {
        console.error("Failed to destroy HLS instance:", error);
    }

    audioController.hls = null;
};

const updateMediaSessionMetadata = (track) => {
    if (!track || !("mediaSession" in navigator) || !("MediaMetadata" in window)) return;

    navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title,
        artist: track.artist,
        artwork: [
            {
                src: `${track.artwork}?width=1000,height=1000`,
                sizes: "1000x1000",
            },
            {
                src: `${track.artwork}?width=${artworkSize},height=${artworkSize}`,
                sizes: `${artworkSize}x${artworkSize}`,
            },
            {
                src: `${track.artwork}?width=250,height=250`,
                sizes: "250x250",
            },
            {
                src: `${track.artwork}?width=125,height=125`,
                sizes: "125x125",
            },
        ],
    });
};

export default function useAudioPlayer() {
    const { playerState, setPlayerState } = useContext(PlayerContext);
    const { audio, volume, isLoading, isPlaying, initData } = playerState;
    const programDateTimeRef = useRef(null);
    const programDateTimeReceivedAtRef = useRef(null);

    const setPlaybackState = useCallback((state) => {
        setPlayerState((previousState) => ({
            ...previousState,
            ...state,
        }));
    }, [setPlayerState]);

    const applyProgramDateTime = useCallback((programDateTime) => {
        if (Number.isNaN(programDateTime.getTime())) return;

        setPlayerState((previousState) => ({
            ...previousState,
            programDateTime,
            initData: syncSpaceToProgramDateTime(previousState.initData, programDateTime),
        }));
    }, [setPlayerState]);

    const captureProgramDateTime = useCallback((programDateTimeValue) => {
        if (!programDateTimeValue) return;

        const programDateTime = new Date(programDateTimeValue);
        if (Number.isNaN(programDateTime.getTime())) return;

        programDateTimeRef.current = programDateTime;
        programDateTimeReceivedAtRef.current = Date.now();
        applyProgramDateTime(programDateTime);
    }, [applyProgramDateTime]);

    const stop = useCallback(() => {
        destroyHls(audio);

        if (audio.current) {
            audio.current.pause();
            audio.current.removeAttribute("src");
            audio.current.load();
        }

        setPlaybackState({
            isPlaying: false,
            isLoading: false,
        });
    }, [audio, setPlaybackState]);

    const play = useCallback(() => {
        if (isLoading) return;

        const audioElement = getAudioElement(audio, volume);

        setPlaybackState({ isLoading: true });

        if (!Hls.isSupported()) {
            setPlaybackState({ isLoading: false });
            return;
        }

        destroyHls(audio);

        const hls = new Hls({ backBufferLength: 0 });
        audio.hls = hls;
        audioElement.onplay = () => {
            if (hls.liveSyncPosition) {
                audioElement.currentTime = hls.liveSyncPosition;
            }
        };

        hls.loadSource(streamUrl);
        hls.attachMedia(audioElement);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
            audioElement.play()
                .then(() => setPlaybackState({ isPlaying: true, isLoading: false }))
                .catch((error) => {
                    console.error("HLS playback failed:", error);
                    setPlaybackState({ isPlaying: false, isLoading: false });
                });
        });

        hls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
            captureProgramDateTime(data?.frag?.programDateTime);
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
            if (!data?.fatal) return;

            if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                hls.recoverMediaError();
                return;
            }

            console.error("Fatal HLS error:", data);
            stop();
        });
    }, [audio, captureProgramDateTime, isLoading, setPlaybackState, stop, volume]);

    const toggle = useCallback(() => {
        if (isPlaying) {
            stop();
            return;
        }

        play();
    }, [isPlaying, play, stop]);

    useEffect(() => {
        if (audio?.current) {
            audio.current.volume = volume;
        }
    }, [audio, volume]);

    useEffect(() => {
        updateMediaSessionMetadata(initData?.current_track);
    }, [initData?.current_track]);

    useEffect(() => {
        if (!isPlaying) return undefined;

        const intervalId = window.setInterval(() => {
            if (!programDateTimeRef.current || !programDateTimeReceivedAtRef.current) return;

            const elapsedMs = Date.now() - programDateTimeReceivedAtRef.current;
            applyProgramDateTime(new Date(programDateTimeRef.current.getTime() + elapsedMs));
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [applyProgramDateTime, isPlaying]);

    useEffect(() => {
        if (!("mediaSession" in navigator)) return;

        navigator.mediaSession.setActionHandler("play", play);
        navigator.mediaSession.setActionHandler("pause", stop);
    }, [play, stop]);

    return {
        isLoading,
        isPlaying,
        play,
        stop,
        toggle,
    };
}
